/**
 * Single source of truth for all supported file extensions.
 * Organized by media type for easy maintenance and extension.
 *
 * @module media-extensions
 */

// ============================================================================
// IMAGE EXTENSIONS
// ============================================================================

/** Image extensions supported by VI transcoders (can encode and decode) */
export const IMAGE_TRANSCODER_EXTENSIONS = [
    'HEIC', 'HEIF', 'JFIF', 'JPEG', 'PNG', 'TIF', 'TIFF', 'WEBP', 'JPG'
] as const;

/** Image extensions supported by VI decoders only (can decode, output to other formats) */
export const IMAGE_DECODER_EXTENSIONS = [
    'AVIF', 'BMP', 'EPS', 'GIF', 'ICO'
] as const;

/** Image extensions supported by IM transcoders */
export const IMAGE_IM_TRANSCODER_EXTENSIONS = [
    'PPM', 'PSD'
] as const;

/** RAW image formats supported by IM decoders */
export const IMAGE_RAW_EXTENSIONS = [
    '3FR', 'ARW', 'CR2', 'CR3', 'CRW', 'DCR', 'DNG', 'ERF',
    'MOS', 'MRW', 'NEF', 'ORF', 'PEF', 'RAF', 'RW2', 'X3F', 'XCF'
] as const;

/** All supported image extensions */
export const IMAGE_EXTENSIONS = [
    ...IMAGE_TRANSCODER_EXTENSIONS,
    ...IMAGE_DECODER_EXTENSIONS,
    ...IMAGE_IM_TRANSCODER_EXTENSIONS,
    ...IMAGE_RAW_EXTENSIONS
] as const;

// ============================================================================
// AUDIO EXTENSIONS
// ============================================================================

/** Audio extensions supported by FF transcoders (can encode and decode) */
export const AUDIO_TRANSCODER_EXTENSIONS = [
    'AAC', 'AC3', 'AIF', 'AIFF', 'AIFC', 'AMR', 'AU', 'DSS',
    'FLAC', 'M4A', 'M4B', 'MP3', 'OGA', 'OGG', 'OPUS', 'WAV', 'WEBA', 'WMA'
] as const;

/** Audio extensions supported by FF decoders only */
export const AUDIO_DECODER_EXTENSIONS = [
    'VOC'
] as const;

/** All supported audio extensions */
export const AUDIO_EXTENSIONS = [
    ...AUDIO_TRANSCODER_EXTENSIONS,
    ...AUDIO_DECODER_EXTENSIONS
] as const;

// ============================================================================
// VIDEO EXTENSIONS
// ============================================================================

/** Video extensions supported by FF transcoders (can encode and decode) */
export const VIDEO_TRANSCODER_EXTENSIONS = [
    '3G2', '3GP', 'ASF', 'AV1', 'AVCHD', 'AVI', 'DIVX', 'F4V', 'FLV',
    'HEVC', 'M2TS', 'M2V', 'M4V', 'MJPEG', 'MKV', 'MOV', 'MP4',
    'MPEG', 'MPEG2', 'MPG', 'MTS', 'MXF', 'OGV', 'RM', 'RMVB',
    'SWF', 'TS', 'VOB', 'WEBM', 'WMV', 'WTV', 'XVID'
] as const;

/** Video extensions supported by FF decoders only */
export const VIDEO_DECODER_EXTENSIONS = [
    '3GPP', 'CAF', 'CAVS', 'DV', 'DVR'
] as const;

/** All supported video extensions */
export const VIDEO_EXTENSIONS = [
    ...VIDEO_TRANSCODER_EXTENSIONS,
    ...VIDEO_DECODER_EXTENSIONS
] as const;

// ============================================================================
// DOCUMENT & OTHER EXTENSIONS
// ============================================================================

/** Archive extensions */
export const ARCHIVE_EXTENSIONS = [
    '7Z', 'RAR', 'TAR', 'TAR.GZ', 'TAR.XZ', 'TAR.BZ2', 'TGZ', 'TB2', 'TZ2', 'ZIP'
] as const;

/** Document extensions */
export const DOCUMENT_EXTENSIONS = ['PDF', 'DOC', 'DOCX', 'PS'] as const;

/** Ebook extensions */
export const EBOOK_EXTENSIONS = ['EPUB', 'MOBI'] as const;

/** Presentation extensions */
export const PRESENTATION_EXTENSIONS = ['PPT', 'PPTX'] as const;

/** Vector extensions */
export const VECTOR_EXTENSIONS = ['SVG', 'PS'] as const;

/** PDF extension */
export const PDF_EXTENSIONS = ['PDF'] as const;

/** PostScript extension */
export const PS_EXTENSIONS = ['PS'] as const;

/** MS Office extensions (for backward compatibility) */
export const MS_OFFICE_EXTENSIONS = [
    ...PRESENTATION_EXTENSIONS,
    'XLS', 'XLT', 'XLM', 'XLSX', 'XLSM', 'XLTX', 'XLTM',
    'DOC', 'DOT', 'WBK', 'DOCX', 'DOCM', 'DOTX', 'DOTM', 'DOCB'
] as const;

// ============================================================================
// AGGREGATED EXTENSION SETS
// ============================================================================

/** All supported extensions across all media types */
export const ALL_EXTENSIONS = [
    ...IMAGE_EXTENSIONS,
    ...AUDIO_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
    ...ARCHIVE_EXTENSIONS,
    ...DOCUMENT_EXTENSIONS,
    ...EBOOK_EXTENSIONS,
    ...PRESENTATION_EXTENSIONS,
    ...VECTOR_EXTENSIONS,
    ...MS_OFFICE_EXTENSIONS,
] as const;

/** Set of all supported extensions for O(1) lookup */
export const ALL_EXTENSIONS_SET = new Set(ALL_EXTENSIONS);

/** Extensions that support thumbnail generation */
export const THUMBNAIL_SUPPORTED_EXTENSIONS = [
    ...IMAGE_EXTENSIONS,
    ...PDF_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
] as const;

/** Set of thumbnail-supported extensions for O(1) lookup */
export const THUMBNAIL_SUPPORTED_EXTENSIONS_SET = new Set(THUMBNAIL_SUPPORTED_EXTENSIONS);

// ============================================================================
// DISALLOWED EXTENSIONS (Security)
// ============================================================================

/** Extensions that are not allowed for security reasons */
export const DISALLOWED_EXTENSIONS = new Set([
    // Executables and scripts
    'ADE', 'ADP', 'APK', 'APPX', 'APPXBUNDLE', 'BAT', 'CAB', 'CHM', 'CMD', 'COM', 'CPL',
    'DIAGCAB', 'DIAGCFG', 'DIAGPACK', 'DLL', 'DMG', 'EX', 'EX_', 'EXE', 'HTA', 'IMG',
    'INS', 'ISO', 'ISP', 'JAR', 'JNLP', 'JS', 'JSE', 'LIB', 'LNK', 'MDE', 'MSC', 'MSI',
    'MSIX', 'MSIXBUNDLE', 'MSP', 'MST', 'NSH', 'PIF', 'PS1', 'SCR', 'SCT', 'SHB', 'SYS',
    'VB', 'VBE', 'VBS', 'VHD', 'VXD', 'WSC', 'WSF', 'WSH', 'XLL',
    // Backup and config files
    'BAK', 'BCK', 'BKP', 'CFG', 'CONF', 'CONFIG', 'INI', 'LOG', 'OLD', 'SAV', 'SAVE',
    'TEMP', 'TMP', 'BIN', 'CGI', 'SHTM', 'SHTML', 'STM',
    // Certificate and key files
    'CER', 'CRT', 'DER', 'KEY', 'P12', 'P7B', 'P7C', 'PEM', 'PFX',
    // Other potentially dangerous files
    'DAT', 'EML', 'HTR', 'HTW', 'IDA', 'IDC', 'IDQ', 'NWS', 'POL', 'PRINTER', 'REG', 'WMZ'
]);

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ImageExtension = typeof IMAGE_EXTENSIONS[number];
export type AudioExtension = typeof AUDIO_EXTENSIONS[number];
export type VideoExtension = typeof VIDEO_EXTENSIONS[number];
export type ArchiveExtension = typeof ARCHIVE_EXTENSIONS[number];
export type DocumentExtension = typeof DOCUMENT_EXTENSIONS[number];
export type AllExtension = typeof ALL_EXTENSIONS[number];
